import React, { useState, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import EditorImg from 'lowcode/components/EditorImg';
import { useStore, useDispatch } from '../context';
import { injectNotes } from '../utils';
import NoteModal from './RuleModal';
import NoAward from './NoAward';
import CouponModal from './CouponModal';
import LuckyWheel from './LuckyWheel';
import FlopGame from './FlopGame';
import FlopGameSecond from './FlopGameSecond';
import PointsModal from './PointsModal';
import css from './index.module.scss';
import Button from '@/components/UI/Button';
import EFArrowRight from '@/components/UI/svg/Icons/EFArrowRight';
import { useGlobal } from '@/utils/useGlobal';
import NextImage from '@/components/NextImage';
import { euLocaleList } from '@/constants/appreciationDay';
import { signupCookie } from '@/utils/cookies/signupCookie';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';

interface LuckyTurntableProps {
    isLogin: boolean;
    setShowPage: (page: string) => void;
    refershData?: () => void;
    classNameStr?: string;
    gtmActiveFun?: () => void;
}

const LuckyTurntable: React.FC<LuckyTurntableProps> = ({ isLogin, setShowPage, refershData, classNameStr, gtmActiveFun }) => {
    const { isMobile, useLang, useRouter } = useGlobal();
    const dispatch = useDispatch();
    const { LUCKY_ACTIVE: luckyActivety, inBlackList } = useStore();
    const { t } = useLang('ecocredits');
    const router = useRouter();
    const { locale, route } = router;
    const [currNotes, setCurrNotes] = useState<any>();
    const [winnerPrize, setWinnerPrize] = useState<any>();
    const [isValidTime, setIsValidTime] = useState(true);
    const timer = useRef<number>();
    const pathUrl = `/${locale}${route}`;
    const isEu = euLocaleList.includes(locale);
    const { timezoneList } = useMemberTimeStore();

    const [startFlop, SetStartFlop] = useState(false);

    // debug 类型3
    // if (luckyActivety) luckyActivety.playWays = 3;

    const getRemainingTimesText = () => {
        const { limitCount, limitDay, playWays } = luckyActivety || {};

        // 活动期间
        if (limitDay === -1) {
            if (playWays !== 1) {
                return t.rich('limit_times_in_active2', { count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i> });
            }
            return t.rich('limit_times_in_active', { count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i> });
        }

        // 不限时间参与
        if (limitDay === 1000000000) {
            if (playWays !== 1) {
                return t.rich('without_limit_times2', { count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i> });
            }
            return t.rich('without_limit_times', { count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i> });
        }

        const day = Math.round(limitDay / 60 / 24);
        // 韩法单天文案调整
        if (locale === 'kr' && day <= 1) {
            return t.rich('limit_times_in_day_one', {
                count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i>,
            });
        }
        if (locale === 'fr' && day <= 1) {
            return t.rich('limit_times_in_day_one', {
                count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i>,
                day: () => <i className={css['remaining-times-count']}>{day}</i>,
            });
        }
        if (playWays !== 1) {
            return t.rich('limit_times_in_day2', {
                count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i>,
                day: () => <i className={css['remaining-times-count']}>{day}</i>,
            });
        }
        return t.rich('limit_times_in_day', {
            count: () => <i className={css['remaining-times-count']}>{limitCount || 0}</i>,
            day: () => <i className={css['remaining-times-count']}>{day}</i>,
        });
    };

    const openRuleModal = () => {
        const ruleList = luckyActivety?.description?.split('\n')?.filter((str: string) => !!str);
        setCurrNotes(ruleList || []);
    };

    const getIsValidTime = () => {
        if (!luckyActivety) return;
        const { startTime, endTime } = luckyActivety;
        const startTimestamp = Number(startTime);
        const endTimestamp = Number(endTime);

        // 不能转换成时间戳，不做处理
        if (Number.isNaN(startTimestamp) || Number.isNaN(endTimestamp)) {
            return;
        }

        const now = new Date().valueOf();
        const newIsValidTime = startTimestamp < now && endTimestamp > now;
        if (isValidTime !== newIsValidTime) {
            setIsValidTime(newIsValidTime);
        }
    };

    const handleAddNotes = (coupon: any) => {
        if (coupon.ruleType === 'COUPONS') {
            return injectNotes([coupon], t, locale, timezoneList)[0];
        }

        if (coupon?.ruleDescription) {
            const noteList = coupon?.ruleDescription?.split('\n')?.filter((str: string) => !!str);
            return {
                ...coupon,
                noteList,
            };
        }
    };

    useEffect(() => {
        timer.current = window.setInterval(getIsValidTime, 500);
        return () => {
            timer.current && window.clearInterval(timer.current);
        };
    }, [luckyActivety, isValidTime]);

    useEffect(() => {
        dispatch({
            type: 'getActives',
            payload: { locale, pathUrl },
        });
    }, []);

    const getLuckyTurntableClass = () => {
        let className = `${css['lucky-turntable']} ${classNameStr ? classNameStr : ''}`;
        if (isEmpty(luckyActivety)) className += ` ${css['hide']}`;
        if (luckyActivety?.playWays === 2) className += ' lucky-flopgame';
        if (luckyActivety?.playWays === 3) className += ' lucky-flopgame-second';
        return className;
    };

    return (
        <>
            <div id="LUCKY_TURNTABLE" className={getLuckyTurntableClass()}>
                {luckyActivety?.playWays === 3 && !isEu && (
                    <div className={css['lucky-background']}>
                        {startFlop ? (
                            <NextImage src={require(`@publicLocal/ecocredits/${isMobile ? 'h5' : 'pc'}/flopbg-second-hover.jpg`)} alt="" />
                        ) : (
                            <NextImage src={require(`@publicLocal/ecocredits/${isMobile ? 'h5' : 'pc'}/flopbg-second.jpg`)} alt="" />
                        )}
                    </div>
                )}
                {luckyActivety?.playWays === 3 && isEu && (
                    <div className={css['lucky-background']}>
                        {startFlop ? (
                            <EditorImg
                                pc="https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1715657971217/%E5%8D%A1%E7%89%87%E5%B1%95%E5%BC%80-hover%E7%8A%B6%E6%80%81%E4%B8%BA%E6%94%BE%E5%A4%A7.jpg"
                                h5="https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1715657983156/%E5%8D%A1%E7%89%87%E5%B1%95%E5%BC%80.jpg"
                                alt=""
                            />
                        ) : (
                            <EditorImg
                                pc="https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1717732941677/PC.jpg"
                                h5="https://ecoflow-service-us-cdn.ecoflow.com/upload/image/1717732911235/M%20(1).png"
                                alt=""
                                i
                            />
                        )}
                    </div>
                )}
                <div className={css['lucky-content']}>
                    <div className={css['lucky-left']}>
                        <div className={css['lucky-left-text-content']}>
                            <h5 className={css['lucky-title']}>{luckyActivety?.title}</h5>

                            <p className={css['lucky-desc']}>{luckyActivety?.subTitle}</p>

                            {!!luckyActivety?.limitFlag && <p className={css['lucky-limit-rule']}>{getRemainingTimesText()}</p>}

                            {luckyActivety?.type === 2 && (
                                <p className={css['lucky-desc2']}>
                                    {luckyActivety?.playWays !== 1
                                        ? t('join_lucky_with_points2', { points: luckyActivety?.usePoints || 0 })
                                        : t('join_lucky_with_points', { points: luckyActivety?.usePoints || 0 })}
                                </p>
                            )}

                            {/* pc: Learn More */}
                            {!!luckyActivety?.limitFlag && luckyActivety?.playWays === 3 && isEu && (
                                <p className={css['flop_times']}>
                                    {t.rich('remaining_number2', {
                                        code: () => <i className="flop_times_counts">{luckyActivety?.available || 0}</i>,
                                    })}
                                </p>
                            )}
                            {/* mobile: 剩余抽奖次数 */}
                            {(!isMobile || luckyActivety?.playWays !== 1) && !!luckyActivety?.description && (
                                <div onClick={openRuleModal} className={css['lucky-rule-more']}>
                                    {t('learn_lucky_rule')}
                                    <EFArrowRight />
                                </div>
                            )}
                            {luckyActivety?.playWays === 1 && (locale === 'us' || locale === 'ca') && (
                                <div className={css['lucky-left-tools']}>
                                    <Button
                                        className={css['rewards_list']}
                                        onClick={() => {
                                            setShowPage('lucky_history');
                                            refershData && refershData();
                                        }}
                                    >
                                        {t('lucky_rewards_list')}
                                    </Button>
                                    <Button
                                        className={css['my_rewards']}
                                        onClick={() => {
                                            if (!isLogin) {
                                                signupCookie?.({
                                                    sector_name: t('rewards_redeemed'),
                                                    entry_position: 4,
                                                });
                                                router.push(`/${locale}/login?redirect=${window.location.href}`);
                                                return;
                                            }
                                            setShowPage('coupon_modal');
                                            refershData && refershData();
                                        }}
                                    >
                                        {t('rewards_redeemed')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={css['lucky-right']}>
                        {luckyActivety?.playWays === 1 && (
                            <div className={css['lucky-panel-content']}>
                                <LuckyWheel
                                    disabled={inBlackList || !isValidTime || (luckyActivety?.limitFlag && !luckyActivety?.available)}
                                    isLogin={isLogin}
                                    prizes={luckyActivety?.drawAwards || []}
                                    drawInfo={luckyActivety}
                                    gtmActiveFun={gtmActiveFun}
                                    onEndRotating={(winnerPrize) => {
                                        if (winnerPrize) {
                                            setWinnerPrize(winnerPrize);
                                        }

                                        dispatch({
                                            type: 'getActives',
                                            payload: { locale, pathUrl },
                                        });
                                        refershData?.();
                                    }}
                                    onCheckError={() => {
                                        dispatch({
                                            type: 'getActives',
                                            payload: { locale, pathUrl },
                                        });
                                        refershData?.();
                                    }}
                                    pathUrl={pathUrl}
                                />
                            </div>
                        )}
                        {luckyActivety?.playWays === 2 && (
                            <FlopGame
                                disabled={inBlackList || !isValidTime || (luckyActivety?.limitFlag && !luckyActivety?.available)}
                                isLogin={isLogin}
                                prizes={luckyActivety?.drawAwards || []}
                                drawInfo={luckyActivety}
                                onEnd={(winnerPrize: any) => {
                                    if (winnerPrize) {
                                        console.log('winnerPrize', winnerPrize);
                                        setWinnerPrize(winnerPrize);
                                    }

                                    dispatch({
                                        type: 'getActives',
                                        payload: { locale, pathUrl },
                                    });
                                    refershData?.();
                                }}
                                onCheckError={() => {
                                    dispatch({
                                        type: 'getActives',
                                        payload: { locale, pathUrl },
                                    });
                                    refershData?.();
                                }}
                                pathUrl={pathUrl}
                            />
                        )}
                        {luckyActivety?.playWays === 3 && (
                            <FlopGameSecond
                                disabled={inBlackList || !isValidTime || (luckyActivety?.limitFlag && !luckyActivety?.available)}
                                isLogin={isLogin}
                                prizes={luckyActivety?.drawAwards || []}
                                drawInfo={luckyActivety}
                                onStart={() => SetStartFlop(true)}
                                onEnd={(winnerPrize: any) => {
                                    if (winnerPrize) {
                                        console.log('winnerPrize', winnerPrize);
                                        setWinnerPrize(winnerPrize);
                                    }
                                    SetStartFlop(false);
                                    dispatch({
                                        type: 'getActives',
                                        payload: { locale, pathUrl },
                                    });
                                    refershData?.();
                                }}
                                onCheckError={() => {
                                    dispatch({
                                        type: 'getActives',
                                        payload: { locale, pathUrl },
                                    });
                                    refershData?.();
                                }}
                                pathUrl={pathUrl}
                            />
                        )}

                        {/* pc: 剩余抽奖次数 */}
                        {/* mobile: Learn More */}
                        {!!luckyActivety?.limitFlag && luckyActivety?.playWays === 1 && (
                            <p className={css['remaining-times-text']}>
                                {t.rich('remaining_number', {
                                    code: () => <i className={css['remaining-times-count']}>{luckyActivety?.available || 0}</i>,
                                })}
                            </p>
                        )}
                        {isMobile && !!luckyActivety?.description && luckyActivety?.playWays === 1 && (
                            <div onClick={openRuleModal} className={css['lucky-rule-more']}>
                                {t('learn_lucky_rule')}
                                <EFArrowRight />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 活动规则弹框 */}
            <NoteModal currNotes={currNotes} setCurrNotes={setCurrNotes} />

            {/* 未中奖弹框 */}
            <NoAward
                desc={winnerPrize?.tips}
                open={(winnerPrize?.prizeType === 'NOTHING' || winnerPrize?.type === 'NOTHING') && luckyActivety?.playWays === 1}
                onCancel={() => {
                    setWinnerPrize(undefined);
                }}
            />

            {/* 优惠券弹框 */}
            {['THIRD_COUPONS', 'COUPONS'].includes(winnerPrize?.prizeType) && (
                <CouponModal
                    open={['THIRD_COUPONS', 'COUPONS'].includes(winnerPrize?.prizeType)}
                    coupon={handleAddNotes(winnerPrize?.rewardRedeemRespVo)}
                    setShowPage={setShowPage}
                    onCancel={() => {
                        setWinnerPrize(undefined);
                    }}
                />
            )}

            {/* 积分弹框 */}
            <PointsModal
                open={winnerPrize?.prizeType === 'INTEGRAL'}
                setShowPage={setShowPage}
                onCancel={() => {
                    setWinnerPrize(undefined);
                }}
            />
        </>
    );
};

export default LuckyTurntable;
